import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Sparkles } from 'lucide-react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { WelcomeStep } from './onboarding/WelcomeStep';
import { ChoiceStep } from './onboarding/ChoiceStep';
import { TaskSelectionStep } from './onboarding/TaskSelectionStep';
import { ChatTaskGuide } from './onboarding/ChatTaskGuide';
import { USPTOTaskGuide } from './onboarding/USPTOTaskGuide';
import { OfficeActionTaskGuide } from './onboarding/OfficeActionTaskGuide';
import { LawsTaskGuide } from './onboarding/LawsTaskGuide';
import { DraftsTaskGuide } from './onboarding/DraftsTaskGuide';
import { FeaturesStep } from './onboarding/FeaturesStep';
import { SessionStep } from './onboarding/SessionStep';
import { useUserStore } from '../stores/userStore';

type OnboardingStep = 'welcome' | 'choice' | 'task-selection' | 'task-guide' | 'features' | 'sessions';

interface OnboardingWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sessionContext: any;
  onPasteQuestion: (question: string) => void;
  onOpenDocumentModal: () => void;
  onOpenUploadModal: () => void;
  onNavigateToPage: (page: 'uspto_data' | 'resources' | 'examination' | 'tasks', applicationNumber?: string, taskId?: string) => void;
  onAddApplication: (appNumber: string) => Promise<void>;
  onSaveSession?: () => void;
}

const STEP_TITLES: Record<OnboardingStep, string> = {
  welcome: 'Welcome',
  choice: 'Choose Your Goal',
  'task-selection': 'Browse Tasks',
  'task-guide': 'Getting Started',
  features: 'App Overview',
  sessions: 'Manage Sessions'
};

export function OnboardingWizard({
  open,
  onOpenChange,
  sessionContext,
  onPasteQuestion,
  onOpenDocumentModal,
  onOpenUploadModal,
  onNavigateToPage,
  onAddApplication,
  onSaveSession
}: OnboardingWizardProps) {
  const [currentStep, setCurrentStep] = useState<OnboardingStep>('welcome');
  const [selectedChoice, setSelectedChoice] = useState<string | undefined>(undefined);
  const [selectedTask, setSelectedTask] = useState<string | undefined>(undefined);
  const [selectedApplicationNumber, setSelectedApplicationNumber] = useState<string | undefined>(undefined);
  const updateUserSettings = useUserStore(state => state.updateUserSettings);

  // Reset wizard state when dialog opens
  useEffect(() => {
    if (open) {
      setCurrentStep('welcome');
      setSelectedChoice(undefined);
      setSelectedTask(undefined);
      setSelectedApplicationNumber(undefined);
    }
  }, [open]);

  // Determine step flow based on selected choice
  const getStepFlow = (): OnboardingStep[] => {
    if (selectedChoice === 'tour') {
      return ['welcome', 'choice', 'features', 'sessions'];
    }
    if (selectedChoice === 'browse_tasks') {
      return ['welcome', 'choice', 'task-selection'];
    }
    return ['welcome', 'choice', 'task-guide'];
  };

  const stepFlow = getStepFlow();
  const currentStepIndex = stepFlow.indexOf(currentStep);
  const progress = ((currentStepIndex + 1) / stepFlow.length) * 100;
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === stepFlow.length - 1;

  const handleNext = () => {
    if (!isLastStep) {
      const nextStep = stepFlow[currentStepIndex + 1];
      setCurrentStep(nextStep);
    }
  };

  const handleBack = () => {
    if (!isFirstStep) {
      const prevStep = stepFlow[currentStepIndex - 1];
      setCurrentStep(prevStep);
    }
  };

  const handleSkip = () => {
    updateUserSettings('hasSeenOnboarding', true);
    onOpenChange(false);
  };

  const handleFinish = () => {
    updateUserSettings('hasCompletedOnboarding', true);
    updateUserSettings('hasSeenOnboarding', true);
    onOpenChange(false);

    // Navigate to appropriate page based on selected choice
    if (selectedChoice === 'browse_tasks' && selectedTask) {
      onNavigateToPage('tasks', undefined, selectedTask);
    } else if (selectedChoice === 'uspto') {
      onNavigateToPage('uspto_data');
    } else if (selectedChoice === 'laws') {
      onNavigateToPage('resources');
    } else if (selectedChoice === 'office_action') {
      onNavigateToPage('examination', selectedApplicationNumber);
    } else if (selectedChoice === 'drafts') {
      onNavigateToPage('drafts' as any);
    }
  };

  const handleCloseWizard = () => {
    onOpenChange(false);
  };

  const handleSelectChoice = (choiceId: string) => {
    setSelectedChoice(choiceId);
    // Auto-advance to next step after selection
    setTimeout(() => {
      // Determine next step based on the choice
      if (choiceId === 'tour') {
        setCurrentStep('features');
      } else if (choiceId === 'browse_tasks') {
        setCurrentStep('task-selection');
      } else {
        setCurrentStep('task-guide');
      }
    }, 300);
  };

  const handleSelectTask = (taskId: string) => {
    setSelectedTask(taskId);
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 'welcome':
        return <WelcomeStep onNext={handleNext} />;

      case 'choice':
        return (
          <ChoiceStep
            onSelectChoice={handleSelectChoice}
            selectedChoice={selectedChoice}
          />
        );

      case 'task-selection':
        return (
          <TaskSelectionStep
            onSelectTask={handleSelectTask}
            selectedTask={selectedTask}
          />
        );

      case 'task-guide':
        // Render the appropriate task guide based on selected choice
        switch (selectedChoice) {
          case 'chat':
            return (
              <ChatTaskGuide
                onOpenDocumentModal={onOpenDocumentModal}
                onPasteQuestion={onPasteQuestion}
                onCloseWizard={handleCloseWizard}
              />
            );
          case 'uspto':
            return (
              <USPTOTaskGuide
                onAddApplication={onAddApplication}
                onCloseWizard={handleCloseWizard}
                onNavigateToUSPTO={() => onNavigateToPage('uspto_data')}
                sessionApplications={sessionContext?.session_applications || []}
              />
            );
          case 'office_action':
            return (
              <OfficeActionTaskGuide
                onCloseWizard={handleCloseWizard}
                onAddApplication={onAddApplication}
                onNavigateToExamination={(appNumber) => onNavigateToPage('examination', appNumber)}
                onApplicationSelected={setSelectedApplicationNumber}
                sessionApplications={sessionContext?.session_applications || []}
              />
            );
          case 'laws':
            return (
              <LawsTaskGuide
                onNavigateToResources={() => onNavigateToPage('resources')}
                onCloseWizard={handleCloseWizard}
              />
            );
          case 'drafts':
            return (
              <DraftsTaskGuide
                onNavigateToDrafts={() => onNavigateToPage('drafts' as any)}
                onCloseWizard={handleCloseWizard}
              />
            );
          default:
            return <div>Please select a task from the previous step.</div>;
        }

      case 'features':
        return <FeaturesStep />;

      case 'sessions':
        return <SessionStep onSaveSession={onSaveSession} />;

      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[calc(100%-2rem)] sm:max-w-[700px] md:max-w-[900px] lg:max-w-[1000px] max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-blue-600" />
            </div>
            <DialogTitle className="text-xl">
              {STEP_TITLES[currentStep]}
            </DialogTitle>
          </div>
        </DialogHeader>

        <div className="mb-4">
          <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
            <span>Step {currentStepIndex + 1} of {stepFlow.length}</span>
            <span>{Math.round(progress)}% complete</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        <div className="flex-1 overflow-y-auto px-1 min-h-0">
          {renderStepContent()}
        </div>

        <DialogFooter className="flex items-center justify-between mt-6 sm:justify-between">
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleBack}
              disabled={isFirstStep}
              className="gap-2"
            >
              <ChevronLeft className="w-4 h-4" />
              Back
            </Button>
            {!isFirstStep && (
              <Button
                variant="ghost"
                onClick={handleSkip}
                className="text-gray-600"
              >
                Skip Tour
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            {isLastStep ? (
              <Button onClick={handleFinish} className="gap-2 bg-blue-600 hover:bg-blue-700">
                {currentStep === 'task-guide' ? "I'm Ready!" : currentStep === 'task-selection' ? "I'm Ready!" : 'Start Using Patent Loop'}
                <Sparkles className="w-4 h-4" />
              </Button>
            ) : currentStep === 'choice' && !selectedChoice ? (
              <Button onClick={handleNext} disabled className="gap-2">
                Select an Option Above
                <ChevronRight className="w-4 h-4" />
              </Button>
            ) : currentStep === 'task-selection' && !selectedTask ? (
              <Button onClick={handleNext} disabled className="gap-2">
                Select a Task Above
                <ChevronRight className="w-4 h-4" />
              </Button>
            ) : (
              <Button onClick={handleNext} className="gap-2">
                {currentStep === 'welcome' ? "Let's Get Started" : 'Continue'}
                <ChevronRight className="w-4 h-4" />
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
